import './NpcDrawer.css';
import { NpcCard } from './NpcCard';
import type { NpcSheetData } from '../../types/scenario';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';

/**
 * V7.1 NPC character sheets (2026-06-02) — 6th drawer panel.
 *
 * Lists every NPC in the scenario, grouped by named-NPCs (key_npcs)
 * vs mook archetypes (guards_archetype). Each row is a collapsed
 * NpcCard so the player can browse + expand individually without
 * losing the rest of the list.
 *
 * 2026-06-05 redaction (Bug Hunt Finding #5): TAROT NPCs whose fame
 * recognition hasn't fired yet get spoiler-text redacted by NpcCard.
 * The drawer is the wiring layer: it consults the atmosphere flag
 * `dossierRedactionEnabled` and the `recognizedIds` set passed in
 * by ScenarioRunner, then computes a per-NPC `recognized` boolean
 * and threads it down. Non-TAROT NPCs (allegiance !== 'tarot') are
 * always considered recognized so allies and mooks render raw.
 */

export interface NpcDrawerProps {
  npcs: NpcSheetData[];
  testidPrefix?: string;
  /** Set of TAROT NPC ids whose Fame Recognition has succeeded in
   *  the current scenario (or carried in from a prior scenario via
   *  cross-scenario fame). Defaults to empty when not provided —
   *  combined with the default atmosphere flag this preserves the
   *  pre-1.272.0 "everything visible" behavior for test fixtures
   *  that don't supply the set. */
  recognizedIds?: ReadonlySet<string>;
}

function isAliasRevealEntry(npc: NpcSheetData): boolean {
  return npc.name.endsWith('(revealed alias)');
}

/**
 * Detect whether an NPC's seed data contains a villain-reveal spoiler
 * that the player shouldn't see pre-recognition. Used by the drawer to
 * decide whether to gate the card behind `recognizedIds`. We MUST use
 * pattern detection (not `allegiance`) because the dr_no_adventure
 * seed JSON does NOT tag Ahmal Ben Gazi with `allegiance: 'tarot'` —
 * allegiance is empty for him, but his role string is the spoiler.
 * Mirror of `redactNpcRole` in NpcCard.tsx but at NPC granularity.
 */
function hasSpoilerPattern(npc: NpcSheetData): boolean {
  if (isAliasRevealEntry(npc)) return true;
  if (npc.role.includes('; Actual:')) return true;
  if (npc.role.includes(' Actual:')) return true;
  if (/\s+(—|--)\s+alias '/.test(npc.role)) return true;
  return false;
}

export function NpcDrawer({
  npcs,
  testidPrefix = 'br-scenario-drawer-npcs',
  recognizedIds,
}: NpcDrawerProps) {
  const { dossierRedactionEnabled } = useAtmosphereSettings();

  // 2026-06-05 NPC drawer redaction. NPCs with a detected spoiler
  // pattern are gated behind `recognizedIds`; all other NPCs (allies,
  // M, Q, mooks, plain enemies whose role doesn't reveal anything)
  // are always considered recognized. This preserves the dossier's
  // "TAROT-only redaction" intent while not relying on the
  // `allegiance` field being populated — which the dr_no_adventure
  // seed JSON does not do consistently for villain-with-alias NPCs.
  function isRecognized(npc: NpcSheetData): boolean {
    if (!dossierRedactionEnabled) return true;
    if (!hasSpoilerPattern(npc)) return true;
    return recognizedIds?.has(npc.id) ?? false;
  }

  // Alias-reveal entries (the "(revealed alias)" stub NPCs) are dropped
  // entirely from the named list when not yet recognized; the section
  // count must reflect the post-filter total.
  const visibleNamed = npcs.filter(
    (n) => !n.isMook && (isRecognized(n) || !isAliasRevealEntry(n)),
  );
  const named = visibleNamed;
  const mooks = npcs.filter((n) => n.isMook);

  return (
    <aside
      className="br-scenario-drawer br-npc-drawer"
      data-testid={testidPrefix}
      aria-label="Non-player characters"
    >
      <header>
        <h2 className="br-display">NPCs</h2>
      </header>
      {npcs.length === 0 && (
        <p
          className="br-npc-drawer-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No NPCs in this scenario.
        </p>
      )}
      {named.length > 0 && (
        <>
          <h3
            className="br-npc-drawer-section-header"
            data-testid={`${testidPrefix}-section-named`}
          >
            Named ({named.length})
          </h3>
          {named.map((n) => (
            <NpcCard
              key={n.id}
              npc={n}
              testidPrefix={`${testidPrefix}-item-${n.id}`}
              recognized={isRecognized(n)}
            />
          ))}
        </>
      )}
      {mooks.length > 0 && (
        <>
          <h3
            className="br-npc-drawer-section-header"
            data-testid={`${testidPrefix}-section-mooks`}
          >
            Combat / Mooks ({mooks.length})
          </h3>
          {mooks.map((n) => (
            <NpcCard
              key={n.id}
              npc={n}
              testidPrefix={`${testidPrefix}-item-${n.id}`}
            />
          ))}
        </>
      )}
    </aside>
  );
}

export default NpcDrawer;
